import { Link } from "react-router-dom";
import { Check, X, ArrowRight, Sparkles, Loader2 } from "lucide-react";
import { useState } from "react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const FREE_FEATURES = [
  { text: "Up to 2 QR campaigns", included: true },
  { text: "Dynamic destinations", included: true },
  { text: "Scan analytics + weekly chart", included: true },
  { text: "Team seats & invites", included: true },
  { text: "Campaign folders", included: true },
  { text: "CSV export of scan history", included: false },
  { text: "Custom short-link domain", included: false },
  { text: "Real-time scan-spike alerts", included: false },
];

const PRO_FEATURES = [
  { text: "Unlimited campaigns", included: true },
  { text: "Everything in Free", included: true },
  { text: "CSV export of scan history", included: true },
  { text: "Custom domain (qr.yourbrand.com)", included: true },
  { text: "Real-time scan-spike alerts + email", included: true },
  { text: "Priority support", included: true },
];

export default function Pricing() {
  const { user } = useAuth();
  const { isPro, billing } = useBilling();
  const [cycle, setCycle] = useState("monthly"); // "monthly" | "yearly"
  const [loading, setLoading] = useState(false);

  const lookupKey = cycle === "yearly" ? "qdurator_pro_yearly" : "qdurator_pro_monthly";
  const displayPrice = cycle === "yearly" ? 90 : 9;
  const unitLabel = cycle === "yearly" ? "/yr" : "/mo";
  const monthlyEquivalent = cycle === "yearly" ? "$7.50/mo" : null;

  const upgrade = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-blue-600" data-testid="pricing-eyebrow">
            Simple pricing
          </span>
          <h1
            data-testid="pricing-heading"
            className="mt-3 text-4xl sm:text-5xl font-black tracking-tight text-slate-900"
          >
            Start free.
            <br />
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
              Scale when you're ready.
            </span>
          </h1>
          <p className="mt-5 text-lg text-slate-600">
            No credit card required to try Qdurator. Upgrade any time — cancel with one click.
          </p>

          {/* Cycle toggle */}
          <div className="mt-8 inline-flex items-center bg-slate-100 rounded-full p-1" role="tablist" data-testid="pricing-cycle-toggle">
            <button
              onClick={() => setCycle("monthly")}
              data-testid="pricing-cycle-monthly"
              className={`px-5 py-2 text-sm font-semibold rounded-full transition-colors ${
                cycle === "monthly"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setCycle("yearly")}
              data-testid="pricing-cycle-yearly"
              className={`px-5 py-2 text-sm font-semibold rounded-full transition-colors flex items-center gap-2 ${
                cycle === "yearly"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Yearly
              <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-black tracking-wider">
                2 MONTHS FREE
              </span>
            </button>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Free */}
          <div
            className="relative bg-white border border-slate-200 rounded-2xl p-8"
            data-testid="pricing-free-card"
          >
            <div className="flex items-baseline justify-between">
              <h2 className="text-2xl font-bold text-slate-900">Free</h2>
              {!isPro && (
                <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-bold">
                  CURRENT
                </span>
              )}
            </div>
            <div className="mt-4">
              <span className="text-5xl font-black tracking-tight text-slate-900">$0</span>
              <span className="text-slate-500 ml-1">/mo</span>
            </div>
            <p className="text-sm text-slate-500 mt-2">Great for testing and small projects.</p>

            <ul className="mt-8 space-y-3">
              {FREE_FEATURES.map((f) => (
                <li key={f.text} className="flex items-start gap-3 text-sm">
                  {f.included ? (
                    <Check className="w-5 h-5 text-emerald-500 shrink-0" strokeWidth={2.5} />
                  ) : (
                    <X className="w-5 h-5 text-slate-300 shrink-0" strokeWidth={2.5} />
                  )}
                  <span className={f.included ? "text-slate-700" : "text-slate-400 line-through"}>
                    {f.text}
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-8">
              {!user ? (
                <Link to="/register">
                  <Button variant="outline" className="w-full rounded-xl h-11" data-testid="pricing-free-cta">
                    Get started
                  </Button>
                </Link>
              ) : (
                <Button variant="outline" disabled className="w-full rounded-xl h-11" data-testid="pricing-free-cta">
                  {isPro ? "Downgrade in settings" : "You're on Free"}
                </Button>
              )}
            </div>
          </div>

          {/* Pro */}
          <div
            className="relative bg-gradient-to-br from-slate-900 to-blue-950 border border-slate-800 rounded-2xl p-8 text-white overflow-hidden"
            data-testid="pricing-pro-card"
          >
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-blue-500/30 rounded-full blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="flex items-baseline justify-between">
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-bold">Pro</h2>
                  <span className="px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 text-[10px] font-black uppercase tracking-wider border border-cyan-500/40">
                    Most popular
                  </span>
                </div>
                {isPro && (
                  <span className="px-2.5 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-xs font-bold">
                    ACTIVE
                  </span>
                )}
              </div>
              <div className="mt-4">
                <span className="text-5xl font-black tracking-tight" data-testid="pricing-pro-price">${displayPrice}</span>
                <span className="text-slate-400 ml-1">{unitLabel}</span>
                {monthlyEquivalent && (
                  <div className="text-xs text-slate-400 mt-1">
                    That's just <span className="text-cyan-300 font-semibold">{monthlyEquivalent}</span> — two months on the house.
                  </div>
                )}
              </div>
              <p className="text-sm text-slate-300 mt-2">Everything you need to grow your QR campaigns.</p>

              <ul className="mt-8 space-y-3">
                {PRO_FEATURES.map((f) => (
                  <li key={f.text} className="flex items-start gap-3 text-sm">
                    <Check className="w-5 h-5 text-cyan-300 shrink-0" strokeWidth={2.5} />
                    <span className="text-slate-100">{f.text}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                {isPro ? (
                  <Button disabled className="w-full rounded-xl h-11 bg-emerald-500 text-white">
                    You're on Pro ✨
                  </Button>
                ) : !user ? (
                  <Link to="/register">
                    <Button className="w-full rounded-xl h-11 bg-white hover:bg-slate-100 text-slate-900 font-semibold" data-testid="pricing-pro-cta">
                      Get started
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                ) : (
                  <Button
                    onClick={upgrade}
                    disabled={loading}
                    data-testid="pricing-upgrade-btn"
                    className="w-full rounded-xl h-11 bg-white hover:bg-slate-100 text-slate-900 font-semibold"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-1" />
                        Upgrade to Pro
                      </>
                    )}
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-slate-400 mt-3 text-center">
                Test mode: use card 4242 4242 4242 4242, any future date, any CVC.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-16 text-center">
          <p className="text-sm text-slate-500">
            Questions?{" "}
            <a href="mailto:hello@qdurator.app" className="text-blue-600 hover:underline">
              Talk to us
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
